import type { ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDirectoryPlans, type DirectoryPlanOption } from "./directoryApi";

export interface BillingTerms {
  planId: string;
  planName: string;
  planPrice: string;
  planPeriod: "monthly" | "yearly" | "one_time" | "";
  planSnapshot: Record<string, unknown> | null;
  isComped: boolean;
  priceNote: string;
  setupAmount: string;
  setupDueDate: string;
  monthlyAmount: string;
  yearlyAmount: string;
  monthlyStartDate: string;
  serviceAmount: string;
}

interface Props {
  mode: "new-listing" | "edit-service";
  value: BillingTerms;
  onChange: (value: BillingTerms) => void;
  onPlanChange?: (plan: DirectoryPlanOption | null) => void;
}

const fieldClass =
  "border-sand/60 bg-paper text-ink h-10 rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold";

function todayDate(): string {
  const today = new Date();
  const local = new Date(today.getTime() - today.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function emptyBillingTerms(): BillingTerms {
  const today = todayDate();
  return {
    setupAmount: "",
    planId: "",
    planName: "",
    planPrice: "",
    planPeriod: "",
    planSnapshot: null,
    isComped: false,
    priceNote: "",
    setupDueDate: today,
    monthlyAmount: "",
    yearlyAmount: "",
    monthlyStartDate: today,
    serviceAmount: "",
  };
}

export function billingTermsForPlan(value: BillingTerms, plan: DirectoryPlanOption | null): BillingTerms {
  if (!plan) {
    return {
      ...value,
      planId: "",
      planName: "",
      planPrice: "",
      planPeriod: "",
      planSnapshot: null,
      isComped: false,
      setupAmount: "",
      monthlyAmount: "",
      yearlyAmount: "",
    };
  }
  const period = plan.billing_period;
  const price = String(plan.price_amount);
  return {
    ...value,
    planId: plan.id,
    planName: plan.name,
    planPrice: price,
    planPeriod: period,
    planSnapshot: {
      id: plan.id,
      slug: plan.slug,
      name: plan.name,
      price_amount: plan.price_amount,
      billing_period: plan.billing_period,
      summary: plan.summary,
      features: plan.features,
      directory_priority: plan.directory_priority,
    },
    isComped: plan.price_amount === 0,
    setupAmount: plan.price_amount > 0 && period === "one_time" ? price : "",
    monthlyAmount: plan.price_amount > 0 && period === "monthly" ? price : "",
    yearlyAmount: plan.price_amount > 0 && period === "yearly" ? price : "",
  };
}

export function BillingFields({ mode, value, onChange, onPlanChange }: Props) {
  const plansQuery = useQuery({
    queryKey: ["directory_public_plans_for_contract"],
    queryFn: fetchDirectoryPlans,
    enabled: mode === "new-listing",
    staleTime: 5 * 60 * 1000,
  });

  function set<K extends keyof BillingTerms>(key: K, next: BillingTerms[K]) {
    onChange({ ...value, [key]: next });
  }

  function setEnabled(
    event: ChangeEvent<HTMLInputElement>,
    key: "setupAmount" | "monthlyAmount" | "yearlyAmount" | "serviceAmount",
  ) {
    set(key, event.target.checked ? value[key] || "0.00" : "");
  }

  function selectPlan(planId: string) {
    const plan = plansQuery.data?.find((candidate) => candidate.id === planId);
    onChange(billingTermsForPlan(value, plan ?? null));
    onPlanChange?.(plan ?? null);
  }

  function setComped(isComped: boolean) {
    const price = Number(value.planPrice);
    onChange({
      ...value,
      isComped,
      setupAmount: isComped ? "" : value.planPeriod === "one_time" && price > 0 ? String(price) : "",
      monthlyAmount: isComped ? "" : value.planPeriod === "monthly" && price > 0 ? String(price) : "",
      yearlyAmount: isComped ? "" : value.planPeriod === "yearly" && price > 0 ? String(price) : "",
    });
  }

  const negotiatedAmount =
    value.planPeriod === "yearly"
      ? value.yearlyAmount
      : value.planPeriod === "monthly"
        ? value.monthlyAmount
        : value.setupAmount;
  const hasSpecialPrice = value.isComped
    ? Number(value.planPrice) > 0
    : value.planPrice !== "" &&
      negotiatedAmount !== "" &&
      Number(negotiatedAmount) !== Number(value.planPrice);

  return (
    <fieldset className="border-sand/40 mt-5 rounded-md border p-4">
      <legend className="font-body text-ink px-1 text-sm font-semibold">
        {mode === "edit-service" ? "Cobro por edición" : "Plan y condiciones de cobro"}
      </legend>
      <p className="text-mist mb-3 text-xs">
        Los cobros se registran pendientes y vencen al inicio de cada periodo; no se procesa ningún pago desde
        aquí.
      </p>

      {mode === "edit-service" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-ink flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#C9A24D]"
                checked={value.serviceAmount !== ""}
                onChange={(event) => setEnabled(event, "serviceAmount")}
              />
              Cobrar por esta edición
            </label>
            {value.serviceAmount !== "" ? (
              <label className="text-ink mt-3 flex flex-col gap-1.5 text-sm font-medium">
                Monto por edición (S/)
                <input
                  className={fieldClass}
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={value.serviceAmount}
                  onChange={(event) => set("serviceAmount", event.target.value)}
                  required
                />
              </label>
            ) : null}
          </div>
          <div>
            <label className="text-ink flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#C9A24D]"
                checked={value.monthlyAmount !== ""}
                disabled={value.yearlyAmount !== ""}
                onChange={(event) => setEnabled(event, "monthlyAmount")}
              />
              Configurar o reactivar mensualidad
            </label>
            {value.monthlyAmount !== "" ? (
              <div className="mt-3 grid gap-3">
                <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                  Monto mensual (S/)
                  <input
                    className={fieldClass}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={value.monthlyAmount}
                    onChange={(event) => set("monthlyAmount", event.target.value)}
                    required
                  />
                </label>
                <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                  Primera mensualidad
                  <input
                    className={fieldClass}
                    type="date"
                    value={value.monthlyStartDate}
                    onChange={(event) => set("monthlyStartDate", event.target.value)}
                    required
                  />
                </label>
                <p className="text-mist text-xs">
                  La mensualidad se activa cuando se aprueben los cambios enviados.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
              Plan del directorio
              <select
                className={fieldClass}
                value={value.planId}
                onChange={(event) => selectPlan(event.target.value)}
                disabled={plansQuery.isLoading}
              >
                <option value="">Sin plan / acuerdo personalizado</option>
                {plansQuery.data?.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} ·{" "}
                    {new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(
                      plan.price_amount,
                    )}{" "}
                    /{" "}
                    {plan.billing_period === "monthly"
                      ? "mes"
                      : plan.billing_period === "yearly"
                        ? "año"
                        : "único"}
                  </option>
                ))}
              </select>
              <span className="text-mist text-xs">
                {plansQuery.isLoading
                  ? "Cargando planes publicados…"
                  : plansQuery.error
                    ? "No se pudo cargar el catálogo; puedes registrar un acuerdo personalizado."
                    : "Se muestran los planes publicados del directorio. El precio se puede negociar para cada negocio."}
              </span>
            </label>
            {value.planId ? (
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <p className="font-semibold text-slate-800">Acuerdo: {value.planName}</p>
                <p className="mt-1 text-slate-600">
                  Precio público:{" "}
                  {new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(
                    Number(value.planPrice),
                  )}{" "}
                  {value.planPeriod === "monthly"
                    ? "por mes"
                    : value.planPeriod === "yearly"
                      ? "por año"
                      : "pago único"}
                </p>
                <p className="mt-1 text-xs font-medium text-slate-600">
                  {value.planSnapshot?.directory_priority === 2
                    ? "Incluye máxima prioridad en los resultados."
                    : value.planSnapshot?.directory_priority === 1
                      ? "Incluye prioridad frente al plan Básico."
                      : "Aparece según coincidencia y ubicación."}
                </p>
                {negotiatedAmount !== "" ? (
                  <p className="mt-1 text-slate-600">
                    Precio acordado:{" "}
                    {new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(
                      Number(negotiatedAmount),
                    )}
                    {hasSpecialPrice
                      ? ` · ${Number(negotiatedAmount) < Number(value.planPrice) ? "por debajo" : "por encima"} del público`
                      : " · precio de lista"}
                  </p>
                ) : null}
                {typeof value.planSnapshot?.summary === "string" && value.planSnapshot.summary ? (
                  <p className="mt-1 text-xs text-slate-500">{value.planSnapshot.summary}</p>
                ) : null}
                {Array.isArray(value.planSnapshot?.features) && value.planSnapshot.features.length > 0 ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Incluye: {value.planSnapshot.features.map(String).join(" · ")}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          {hasSpecialPrice && !value.isComped ? (
            <label className="text-ink mb-4 flex flex-col gap-1.5 text-sm font-medium sm:max-w-xl">
              Motivo del precio especial
              <input
                className={fieldClass}
                value={value.priceNote}
                onChange={(event) => set("priceNote", event.target.value)}
                maxLength={240}
                placeholder="Ej. precio de amigo, promoción o servicio adicional"
              />
            </label>
          ) : null}
          <label className="text-ink mb-4 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[#C9A24D]"
              checked={value.isComped}
              onChange={(event) => setComped(event.target.checked)}
            />
            <span>
              <span className="block font-medium">Marcar como cortesía gratuita</span>
              <span className="text-mist block text-xs">
                Conserva el plan y sus beneficios, pero no genera cargos recurrentes ni de alta.
              </span>
            </span>
          </label>
          {value.isComped ? (
            <label className="text-ink mb-4 flex flex-col gap-1.5 text-sm font-medium sm:max-w-xl">
              Motivo de la cortesía
              <input
                className={fieldClass}
                value={value.priceNote}
                onChange={(event) => set("priceNote", event.target.value)}
                maxLength={240}
                placeholder="Ej. negocio de mi mamá / familiar"
                required
              />
            </label>
          ) : null}
          {!value.isComped ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#C9A24D]"
                    checked={value.setupAmount !== ""}
                    onChange={(event) => setEnabled(event, "setupAmount")}
                  />
                  Cobrar por alta de ficha
                </label>
                {value.setupAmount !== "" ? (
                  <div className="mt-3 grid gap-3">
                    <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                      Monto de alta (S/)
                      <input
                        className={fieldClass}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={value.setupAmount}
                        onChange={(event) => set("setupAmount", event.target.value)}
                        required
                      />
                    </label>
                    <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                      Vence el
                      <input
                        className={fieldClass}
                        type="date"
                        value={value.setupDueDate}
                        onChange={(event) => set("setupDueDate", event.target.value)}
                        required
                      />
                    </label>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#C9A24D]"
                    checked={value.monthlyAmount !== ""}
                    onChange={(event) => setEnabled(event, "monthlyAmount")}
                  />
                  Cobrar mensualidad
                </label>
                {value.monthlyAmount !== "" ? (
                  <div className="mt-3 grid gap-3">
                    <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                      Monto mensual (S/)
                      <input
                        className={fieldClass}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={value.monthlyAmount}
                        onChange={(event) => set("monthlyAmount", event.target.value)}
                        required
                      />
                    </label>
                    <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                      Primera mensualidad
                      <input
                        className={fieldClass}
                        type="date"
                        value={value.monthlyStartDate}
                        onChange={(event) => set("monthlyStartDate", event.target.value)}
                        required
                      />
                    </label>
                    {value.planId ? (
                      <p className="text-mist text-xs">
                        El primer cobro queda pendiente desde el inicio del contrato. La renovación se genera
                        al inicio de cada mes.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div>
                <label className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#C9A24D]"
                    checked={value.yearlyAmount !== ""}
                    disabled={value.monthlyAmount !== ""}
                    onChange={(event) => setEnabled(event, "yearlyAmount")}
                  />
                  Cobrar anualidad
                </label>
                {value.yearlyAmount !== "" ? (
                  <div className="mt-3 grid gap-3">
                    <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                      Monto anual (S/)
                      <input
                        className={fieldClass}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={value.yearlyAmount}
                        onChange={(event) => set("yearlyAmount", event.target.value)}
                        required
                      />
                    </label>
                    <label className="text-ink flex flex-col gap-1.5 text-sm font-medium">
                      Inicio de contrato / primer cobro
                      <input
                        className={fieldClass}
                        type="date"
                        value={value.monthlyStartDate}
                        onChange={(event) => set("monthlyStartDate", event.target.value)}
                        required
                      />
                    </label>
                    <p className="text-mist text-xs">
                      Se genera un cobro pendiente al inicio y luego en cada aniversario anual.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </>
      )}
    </fieldset>
  );
}
